import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Save, Plus, Trash2, X } from "lucide-react";
import { Button } from "../../../components/ui/button";
import { legacyApi } from "../../../services/legacyApi";
import { api } from "../../../services/api";
import { toast } from "sonner";
import EntityTypeaheadPicker, { PickerOption } from "../../../components/search/EntityTypeaheadPicker";
import { RequiredLabel } from "../../../components/forms/RequiredLabel";
import { FieldErrorText } from "../../../components/forms/FieldErrorText";
import { showValidationSummaryToast } from "../../../components/forms/ValidationSummaryToast";
import { focusFirstInvalidField, validateRequiredFields } from "../../../components/forms/formValidationUtils";
import { FlowEntryBanner } from "../../../components/workflow/FlowEntryBanner";
import { useAuth } from "../../../context/AuthContext";

interface GRNItem {
    material_id: number;
    description: string;
    uom?: string;
    unit_id?: number;
    transaction_unit_id?: number;
    quantity: number;
}

interface MaterialOption {
    material_id: number;
    name?: string;
    code?: string;
    item_type?: string;
    uom?: string;
    cost_profiles?: string[];
    description?: string;
    base_unit_id?: number | null;
    base_unit_name?: string | null;
    uom_conversions?: Array<{
        from_unit_id: number;
        from_unit_name?: string | null;
        to_unit_id?: number;
        to_unit_name?: string | null;
        factor_to_base: number;
        is_base?: boolean;
    }>;
}

interface MaterialCategoryOption {
    material_category_id: number;
    material_category: string;
}

interface UnitOption {
    unit_id: number;
    unit: string;
}

interface QuickMaterialFormState {
    material_name: string;
    material_category_id: string;
    unit_id: string;
    item_type: "Stock" | "Consumable";
    description: string;
}

const QUICK_MATERIAL_INITIAL: QuickMaterialFormState = {
    material_name: "",
    material_category_id: "",
    unit_id: "",
    item_type: "Stock",
    description: "",
};

const normalizeItemType = (value?: string) => {
    const normalized = String(value || "Stock").replace(/[-_\s]/g, "").toLowerCase();
    if (["consumable", "expense", "nonstock"].includes(normalized)) return "Consumable";
    if (["service", "services"].includes(normalized)) return "Service";
    if (["fixedasset", "asset", "fixedassets"].includes(normalized)) return "FixedAsset";
    return "Stock";
};

export const DirectGRN: React.FC = () => {
    const navigate = useNavigate();
    const { hasPermission } = useAuth();
    const canQuickAddMaterial = hasPermission("inventory.materials.manage");
    const [suppliers, setSuppliers] = useState<any[]>([]);
    const [materials, setMaterials] = useState<MaterialOption[]>([]);
    const [materialCategories, setMaterialCategories] = useState<MaterialCategoryOption[]>([]);
    const [units, setUnits] = useState<UnitOption[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [formData, setFormData] = useState({
        supplier_id: "",
        date: new Date().toISOString().split("T")[0],
        supplier_dn_ref: "",
    });

    const [items, setItems] = useState<GRNItem[]>([]);
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
    const [quickMaterialOpen, setQuickMaterialOpen] = useState(false);
    const [quickMaterialSaving, setQuickMaterialSaving] = useState(false);
    const [quickMaterialTargetRow, setQuickMaterialTargetRow] = useState<number | null>(null);
    const [quickMaterialForm, setQuickMaterialForm] = useState<QuickMaterialFormState>({ ...QUICK_MATERIAL_INITIAL });
    const [quickMaterialErrors, setQuickMaterialErrors] = useState<Record<string, string>>({});

    useEffect(() => {
        loadData();
    }, []);

    const materialPickerOptions = useMemo<PickerOption<string>[]>(() => {
        return materials.map((m) => ({
            value: String(m.material_id),
            label: m.name || `Material ${m.material_id}`,
            description: [m.code, normalizeItemType(m.item_type), m.uom, (m.cost_profiles || []).join("/")].filter(Boolean).join(" | ") || undefined,
            badges: [{ label: normalizeItemType(m.item_type), tone: normalizeItemType(m.item_type) === "Consumable" ? "amber" : "green" }],
        }));
    }, [materials]);

    const normalizeMaterialRows = (matData: any[]): MaterialOption[] => {
        return Array.isArray(matData)
            ? matData.map((m) => ({
                material_id: Number(m.material_id),
                name: m.material_name,
                code: `HC/MTR/${String(m.material_id).padStart(4, "0")}`,
                item_type: normalizeItemType(m.item_type),
                uom: m.base_unit_name || m.unit || "",
                cost_profiles: m.cost_profiles || [],
                description: m.description || "",
                base_unit_id: m.base_unit_id ?? null,
                base_unit_name: m.base_unit_name || m.unit || "",
                uom_conversions: Array.isArray(m.uom_conversions) ? m.uom_conversions : [],
            }))
            : [];
    };

    const getMaterialMeta = (materialId?: number) => materials.find((m) => m.material_id === Number(materialId));

    const getLineConversionPreview = (item: GRNItem) => {
        const material = getMaterialMeta(item.material_id);
        const qty = Number(item.quantity || 0);
        const txUnitId = Number(item.transaction_unit_id || item.unit_id || material?.base_unit_id || 0);
        const conversion = (material?.uom_conversions || []).find((row) => Number(row.from_unit_id) === txUnitId);
        const factor = Number(conversion?.factor_to_base || 1);
        return {
            baseQty: qty * factor,
            baseUnitName: material?.base_unit_name || item.uom || "",
        };
    };

    const loadMaterialOptions = async () => {
        const matData = await api.purchasing.listDirectGrnMaterialOptions();
        setMaterials(normalizeMaterialRows(matData));
        return normalizeMaterialRows(matData);
    };

    const loadData = async () => {
        setIsLoading(true);
        try {
            const [suppData, matData, categoryData, unitData] = await Promise.all([
                legacyApi.suppliers.list(),
                api.purchasing.listDirectGrnMaterialOptions(),
                legacyApi.masterData.list("tbl_material_categories"),
                legacyApi.masterData.listUnits(),
            ]);
            setSuppliers(Array.isArray(suppData) ? suppData : []);
            setMaterials(normalizeMaterialRows(matData));
            setMaterialCategories(Array.isArray(categoryData) ? categoryData : []);
            setUnits(Array.isArray(unitData) ? unitData : []);
        } catch (error) {
            console.error("Failed to load data:", error);
            toast.error("Failed to load suppliers or materials");
        } finally {
            setIsLoading(false);
        }
    };

    const handleAddItem = () => {
        setItems((prev) => [
            ...prev,
            {
                material_id: 0,
                description: "",
                unit_id: undefined,
                transaction_unit_id: undefined,
                quantity: 1,
            },
        ]);
    };

    const handleRemoveItem = (index: number) => {
        const newItems = [...items];
        newItems.splice(index, 1);
        setItems(newItems);
    };

    const handleItemChange = (index: number, field: keyof GRNItem, value: any) => {
        const newItems = [...items];
        const item = { ...newItems[index], [field]: value };

        if (field === "material_id") {
            const selectedMat = materials.find((m) => m.material_id === Number(value));
            if (selectedMat) {
                item.description = selectedMat.name || "";
                item.uom = selectedMat.base_unit_name || selectedMat.uom || "";
                item.unit_id = selectedMat.base_unit_id || item.unit_id;
                item.transaction_unit_id = selectedMat.base_unit_id || item.transaction_unit_id;
            }
        }

        newItems[index] = item;
        setItems(newItems);
    };

    const openQuickMaterialModal = (targetRow?: number) => {
        if (!canQuickAddMaterial) {
            toast.error("You do not have permission to create materials.");
            return;
        }
        let rowIndex = targetRow ?? items.length - 1;
        if (rowIndex < 0) {
            setItems((prev) => [...prev, { material_id: 0, description: "", unit_id: undefined, transaction_unit_id: undefined, quantity: 1 }]);
            rowIndex = 0;
        }
        setQuickMaterialTargetRow(rowIndex);
        setQuickMaterialErrors({});
        setQuickMaterialForm({ ...QUICK_MATERIAL_INITIAL });
        setQuickMaterialOpen(true);
    };

    const closeQuickMaterialModal = () => {
        if (quickMaterialSaving) return;
        setQuickMaterialOpen(false);
        setQuickMaterialTargetRow(null);
        setQuickMaterialErrors({});
        setQuickMaterialForm({ ...QUICK_MATERIAL_INITIAL });
    };

    const handleQuickMaterialSave = async () => {
        const validation = validateRequiredFields([
            { key: "material_name", label: "Material Name", value: quickMaterialForm.material_name, required: true },
            { key: "material_category_id", label: "Category", value: quickMaterialForm.material_category_id, required: true },
            { key: "unit_id", label: "Unit", value: quickMaterialForm.unit_id, required: true },
            { key: "item_type", label: "Item Type", value: quickMaterialForm.item_type, required: true },
        ]);
        if (!validation.valid) {
            setQuickMaterialErrors(validation.errorsByField);
            showValidationSummaryToast();
            return;
        }

        setQuickMaterialSaving(true);
        try {
            const created = await api.materials.create({
                material_name: quickMaterialForm.material_name.trim(),
                material_category_id: Number(quickMaterialForm.material_category_id),
                unit_id: Number(quickMaterialForm.unit_id),
                item_type: quickMaterialForm.item_type,
                description: quickMaterialForm.description.trim() || undefined,
            });
            const createdId = Number(created?.id || 0);
            const refreshed = await loadMaterialOptions();
            const selected = refreshed.find((m) => m.material_id === createdId);
            if (!selected) {
                throw new Error("Material created but failed to reload picker options.");
            }
            const row = quickMaterialTargetRow ?? 0;
            setItems((prev) => {
                const next = [...prev];
                if (!next[row]) next[row] = { material_id: 0, description: "", quantity: 1 };
                next[row] = {
                    ...next[row],
                    material_id: selected.material_id,
                    description: selected.name || next[row].description,
                    uom: selected.base_unit_name || selected.uom || next[row].uom,
                    unit_id: selected.base_unit_id || next[row].unit_id,
                    transaction_unit_id: selected.base_unit_id || next[row].transaction_unit_id,
                };
                return next;
            });
            toast.success(`Material ${selected.name || createdId} created and selected.`);
            closeQuickMaterialModal();
        } catch (error: any) {
            console.error("Failed to create quick material:", error);
            toast.error(error?.message || "Failed to create material");
        } finally {
            setQuickMaterialSaving(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const validation = validateRequiredFields([
            { key: "supplier_id", label: "Supplier", value: formData.supplier_id, required: true },
            { key: "supplier_dn_ref", label: "Supplier DN Ref", value: formData.supplier_dn_ref, required: true },
        ]);
        const nextErrors = { ...validation.errorsByField } as Record<string, string>;
        if (items.length === 0) nextErrors.items = "Please add at least one item";
        items.forEach((item, index) => {
            if (!item.material_id) nextErrors[`item_${index}_material`] = "Material is required";
            if (Number(item.quantity || 0) <= 0) nextErrors[`item_${index}_quantity`] = "Quantity must be greater than zero";
        });
        if (Object.keys(nextErrors).length > 0) {
            setFieldErrors(nextErrors);
            showValidationSummaryToast();
            focusFirstInvalidField(Object.keys(nextErrors)[0]);
            return;
        }
        setFieldErrors({});

        setIsSubmitting(true);
        try {
            const payload = {
                supplier_id: Number(formData.supplier_id),
                date: formData.date,
                reference_no: formData.supplier_dn_ref.trim(),
                items: items.map((item) => ({
                    material_id: Number(item.material_id),
                    description: item.description,
                    quantity: Number(item.quantity),
                    transaction_unit_id: item.transaction_unit_id || item.unit_id || undefined,
                })),
            };

            const result = await api.purchasing.createDirectGRN(payload);
            if (result?.warning) {
                toast.warning(result.warning);
            } else {
                toast.success("Direct GRN Created Successfully");
            }
            navigate("/legacy/grn");
        } catch (error: any) {
            console.error("Failed to submit:", error);
            toast.error(error.message || "Failed to create GRN");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center gap-4">
                <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
                    <ArrowLeft className="h-4 w-4" />
                </Button>
                <div>
                    <h1 className="text-2xl font-bold tracking-tight">Direct GRN</h1>
                    <p className="text-muted-foreground">Receive goods directly without a Purchase Order</p>
                </div>
            </div>

            <FlowEntryBanner
                title="Direct GRN (No PO)"
                pathLabel="Valued Supplier Receipt"
                nextHint="Use only for controlled manual receipts; PO->GRN is preferred for full traceability."
                helpModule="purchasing"
            />

            <div className="bg-card rounded-lg border shadow-sm p-6">
                <form onSubmit={handleSubmit} className="space-y-8">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="space-y-2">
                            <RequiredLabel required htmlFor="supplier_id">Supplier</RequiredLabel>
                            <select
                                id="supplier_id"
                                name="supplier_id"
                                aria-invalid={!!fieldErrors.supplier_id}
                                aria-required="true"
                                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                value={formData.supplier_id}
                                onChange={(e) => setFormData({ ...formData, supplier_id: e.target.value })}
                                disabled={isLoading}
                            >
                                <option value="">Select Supplier...</option>
                                {suppliers.map((s) => (
                                    <option key={s.supplier_id} value={s.supplier_id}>
                                        {s.supplier_name}
                                    </option>
                                ))}
                            </select>
                            <FieldErrorText id="supplier_id_error" message={fieldErrors.supplier_id} />
                        </div>
                        <div className="space-y-2">
                            <label className="text-sm font-medium">Date</label>
                            <input
                                type="date"
                                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                value={formData.date}
                                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                            />
                        </div>
                        <div className="space-y-2">
                            <RequiredLabel required htmlFor="supplier_dn_ref">Supplier DN Ref</RequiredLabel>
                            <input
                                id="supplier_dn_ref"
                                name="supplier_dn_ref"
                                aria-invalid={!!fieldErrors.supplier_dn_ref}
                                aria-required="true"
                                type="text"
                                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                placeholder="DN-12345"
                                value={formData.supplier_dn_ref}
                                onChange={(e) => setFormData({ ...formData, supplier_dn_ref: e.target.value })}
                            />
                            <FieldErrorText id="supplier_dn_ref_error" message={fieldErrors.supplier_dn_ref} />
                        </div>
                    </div>

                    <div className="space-y-4">
                        <div className="flex items-center justify-between">
                            <h3 className="text-lg font-semibold">Items Received</h3>
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => openQuickMaterialModal()}
                                disabled={!canQuickAddMaterial}
                                title={!canQuickAddMaterial ? "Requires inventory.materials.manage permission" : "Create and use a new stock material"}
                            >
                                <Plus className="h-4 w-4 mr-2" />
                                Add New Material
                            </Button>
                        </div>

                        <div className="border rounded-md overflow-visible">
                            <table className="w-full text-sm text-left">
                                <thead className="bg-muted/50 text-muted-foreground">
                                    <tr>
                                        <th className="px-4 py-3 font-medium">Material</th>
                                        <th className="px-4 py-3 font-medium w-24">Base UOM</th>
                                        <th className="px-4 py-3 font-medium w-32">Receive UOM</th>
                                        <th className="px-4 py-3 font-medium w-32">Qty Recv</th>
                                        <th className="px-4 py-3 font-medium w-32 text-right">Base Qty</th>
                                        <th className="px-4 py-3 font-medium w-12"></th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y">
                                    {items.length === 0 ? (
                                        <tr>
                                            <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                                                No items added. Click "Add Item" to start.
                                            </td>
                                        </tr>
                                    ) : (
                                        items.map((item, index) => {
                                            const material = getMaterialMeta(item.material_id);
                                            const preview = getLineConversionPreview(item);
                                            return (
                                            <tr key={index} className="bg-card">
                                                <td className="p-2 align-top">
                                                    <div className="relative z-30">
                                                        <EntityTypeaheadPicker
                                                            value={item.material_id ? String(item.material_id) : ""}
                                                            options={materialPickerOptions}
                                                            placeholder="Search material..."
                                                            emptyText="No matching materials"
                                                            loading={isLoading}
                                                            onChange={(value) => handleItemChange(index, "material_id", Number(value))}
                                                        />
                                                    </div>
                                                    <div className="mt-1">
                                                        <Button
                                                            type="button"
                                                            variant="ghost"
                                                            size="sm"
                                                            className="h-7 px-2 text-xs text-blue-600"
                                                            onClick={() => openQuickMaterialModal(index)}
                                                            disabled={!canQuickAddMaterial}
                                                        >
                                                            + New Material
                                                        </Button>
                                                    </div>
                                                    <FieldErrorText message={fieldErrors[`item_${index}_material`]} />
                                                </td>
                                                <td className="p-2 align-middle text-sm text-muted-foreground">
                                                    {material?.base_unit_name || item.uom || "-"}
                                                </td>
                                                <td className="p-2 align-middle">
                                                    <select
                                                        value={item.transaction_unit_id || material?.base_unit_id || ""}
                                                        onChange={(e) => handleItemChange(index, "transaction_unit_id", Number(e.target.value))}
                                                        className="flex h-9 w-full rounded-md border border-input bg-background px-2 py-1 text-sm"
                                                        disabled={!item.material_id}
                                                    >
                                                        {(material?.uom_conversions || []).map((option) => (
                                                            <option key={`${item.material_id}-${option.from_unit_id}`} value={option.from_unit_id}>
                                                                {option.from_unit_name || option.to_unit_name}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </td>
                                                <td className="p-2">
                                                    <input
                                                        id={`item_${index}_quantity`}
                                                        name={`item_${index}_quantity`}
                                                        aria-invalid={!!fieldErrors[`item_${index}_quantity`]}
                                                        type="number"
                                                        className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
                                                        value={item.quantity}
                                                        onChange={(e) => handleItemChange(index, "quantity", e.target.value)}
                                                        min="0.001"
                                                        step="0.001"
                                                    />
                                                    <FieldErrorText message={fieldErrors[`item_${index}_quantity`]} />
                                                </td>
                                                <td className="p-2 align-middle text-right text-sm text-muted-foreground">
                                                    {preview.baseQty.toFixed(3)} {preview.baseUnitName}
                                                </td>
                                                <td className="p-2 text-center">
                                                    <Button
                                                        type="button"
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                                                        onClick={() => handleRemoveItem(index)}
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                </td>
                                            </tr>
                                        )})
                                    )}
                                </tbody>
                            </table>
                        </div>

                        <div className="flex justify-start">
                            <Button type="button" variant="outline" size="sm" onClick={handleAddItem}>
                                <Plus className="h-4 w-4 mr-2" />
                                Add Item
                            </Button>
                        </div>
                    </div>

                    <div className="flex justify-end pt-4">
                        {fieldErrors.items && (
                            <div className="mr-auto">
                                <FieldErrorText message={fieldErrors.items} />
                            </div>
                        )}
                        <Button type="submit" disabled={isSubmitting || isLoading}>
                            {isSubmitting ? (
                                <>Saving...</>
                            ) : (
                                <>
                                    <Save className="h-4 w-4 mr-2" /> Create GRN
                                </>
                            )}
                        </Button>
                    </div>
                </form>
            </div>

            {quickMaterialOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
                    <div className="w-full max-w-xl rounded-lg border bg-white p-5 shadow-xl">
                        <div className="mb-4 flex items-center justify-between">
                            <h3 className="text-lg font-semibold">Quick Add Material</h3>
                            <Button type="button" variant="ghost" size="icon" onClick={closeQuickMaterialModal} disabled={quickMaterialSaving}>
                                <X className="h-4 w-4" />
                            </Button>
                        </div>
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                            <div className="space-y-2 md:col-span-2">
                                <RequiredLabel required htmlFor="quick_material_name">Material Name</RequiredLabel>
                                <input
                                    id="quick_material_name"
                                    value={quickMaterialForm.material_name}
                                    onChange={(e) => setQuickMaterialForm((prev) => ({ ...prev, material_name: e.target.value }))}
                                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                />
                                <FieldErrorText message={quickMaterialErrors.material_name} />
                            </div>
                            <div className="space-y-2">
                                <RequiredLabel required htmlFor="quick_material_category">Category</RequiredLabel>
                                <select
                                    id="quick_material_category"
                                    value={quickMaterialForm.material_category_id}
                                    onChange={(e) => setQuickMaterialForm((prev) => ({ ...prev, material_category_id: e.target.value }))}
                                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                >
                                    <option value="">Select category...</option>
                                    {materialCategories.map((cat) => (
                                        <option key={cat.material_category_id} value={cat.material_category_id}>
                                            {cat.material_category}
                                        </option>
                                    ))}
                                </select>
                                <FieldErrorText message={quickMaterialErrors.material_category_id} />
                            </div>
                            <div className="space-y-2">
                                <RequiredLabel required htmlFor="quick_material_unit">Unit</RequiredLabel>
                                <select
                                    id="quick_material_unit"
                                    value={quickMaterialForm.unit_id}
                                    onChange={(e) => setQuickMaterialForm((prev) => ({ ...prev, unit_id: e.target.value }))}
                                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                >
                                    <option value="">Select unit...</option>
                                    {units.map((u) => (
                                        <option key={u.unit_id} value={u.unit_id}>
                                            {u.unit}
                                        </option>
                                    ))}
                                </select>
                                <FieldErrorText message={quickMaterialErrors.unit_id} />
                            </div>
                            <div className="space-y-2">
                                <RequiredLabel required htmlFor="quick_material_item_type">Item Type</RequiredLabel>
                                <select
                                    id="quick_material_item_type"
                                    value={quickMaterialForm.item_type}
                                    onChange={(e) => setQuickMaterialForm((prev) => ({ ...prev, item_type: e.target.value as "Stock" | "Consumable" }))}
                                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                >
                                    <option value="Stock">Stock / Storable</option>
                                    <option value="Consumable">Consumable / Expense</option>
                                </select>
                                <p className="text-xs text-muted-foreground">Direct GRN supports Stock and Consumable materials. Consumables are received for audit/billing but not stock-valued.</p>
                            </div>
                            <div className="space-y-2 md:col-span-2">
                                <label className="text-sm font-medium" htmlFor="quick_material_description">Description</label>
                                <textarea
                                    id="quick_material_description"
                                    rows={3}
                                    value={quickMaterialForm.description}
                                    onChange={(e) => setQuickMaterialForm((prev) => ({ ...prev, description: e.target.value }))}
                                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                />
                            </div>
                        </div>
                        <div className="mt-5 flex items-center justify-end gap-2">
                            <Button type="button" variant="outline" onClick={closeQuickMaterialModal} disabled={quickMaterialSaving}>
                                Cancel
                            </Button>
                            <Button type="button" onClick={handleQuickMaterialSave} disabled={quickMaterialSaving}>
                                {quickMaterialSaving ? "Saving..." : "Create Material"}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
